import { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, Calendar, Edit, Home, MapPin, Package, Plus, ShoppingCart, User } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CreateJobModal from '../components/CreateJobModal';
import Header from '../components/Header';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { createDateColumn } from '../components/ui/column-helpers';
import { DataTable } from '../components/ui/data-table';
import { Job } from '../types/job';
import { Order, OrderStatus } from '../types/order';
import { Address } from '../types/shipment';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface OrderItem {
  id: string;
  asset_template_id: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  rental_length_months: number;
  asset_template?: {
    id: string;
    name: string;
    model_number?: string;
    images?: Array<{
      id: string;
      url?: string | null;
      variant: 'web' | 'hires';
      position: number;
    }>;
  };
}

const OrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [createJobModalOpen, setCreateJobModalOpen] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    loadOrderDetails();
  }, [id]);

  const loadOrderDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const [orderRes, itemsRes, jobsRes] = await Promise.all([
        fetch(`/api/orders/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`/api/orders/${id}/items`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`/api/jobs/order/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const orderData = await orderRes.json();
      const itemsData = await itemsRes.json();
      const jobsData = await jobsRes.json();

      if (orderData.success) {
        setOrder(orderData.data);
      }
      if (itemsData.success) {
        setOrderItems(itemsData.data || []);
      }
      if (jobsData.success) {
        setJobs(jobsData.data || []);
      }
    } catch (err) {
      console.error('Error loading order details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleJobCreated = () => {
    loadOrderDetails();
  };

  const getStatusBadgeVariant = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.COMPLETED:
        return 'default' as const;
      case OrderStatus.IN_PROGRESS:
        return 'warning' as const;
      case OrderStatus.CONFIRMED:
        return 'secondary' as const;
      case OrderStatus.CANCELLED:
        return 'destructive' as const;
      case OrderStatus.ON_HOLD:
        return 'outline' as const;
      default:
        return 'secondary' as const;
    }
  };

  const getJobStatusBadge = (status: string) => {
    const statusMap: Record<string, 'default' | 'warning' | 'secondary' | 'destructive'> = {
      completed: 'default',
      in_progress: 'warning',
      pending: 'secondary',
      cancelled: 'destructive',
    };
    return statusMap[status] || 'secondary';
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatAddress = (address?: Address | null) => {
    if (!address) return 'Not specified';
    const parts = [
      address.address_line1,
      address.address_line2,
      address.city,
      address.region,
      address.postal_code,
    ].filter(Boolean);
    return parts.join(', ');
  };

  const calculateTotal = () => {
    return orderItems.reduce((sum, item) => sum + Number(item.total_price), 0);
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getRentalLabel = (months: number) => {
    const options = [
      { value: 1, label: '1 month' },
      { value: 2, label: '2 months' },
      { value: 3, label: '3 months' },
      { value: 6, label: '6 months' },
      { value: 12, label: '12 months' },
      { value: 24, label: '24 months' },
    ];
    return options.find(opt => opt.value === months)?.label || `${months} months`;
  };

  const jobColumns: ColumnDef<Job>[] = [
    {
      accessorKey: 'job_number',
      header: 'Job Number',
      cell: ({ row }) => (
        <button
          onClick={() => navigate(`/jobs/${row.original.id}`)}
          className="font-medium text-gray-900 hover:text-gray-700 underline hover:no-underline"
        >
          {row.original.job_number}
        </button>
      ),
    },
    {
      accessorKey: 'job_type',
      header: 'Type',
      cell: ({ row }) => (
        <span className="capitalize">
          {row.original.job_type
            ? row.original.job_type.replace('_', ' ')
            : <span className="text-muted-foreground italic">Task-based</span>
          }
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={getJobStatusBadge(row.original.status)}>
          {row.original.status.toUpperCase()}
        </Badge>
      ),
    },
    createDateColumn('planned_start_at', 'Planned Start'),
    {
      accessorKey: 'assigned_driver',
      header: 'Driver',
      cell: ({ row }) => {
        const driver = row.original.assigned_driver;
        return driver ? `${driver.first_name} ${driver.last_name}` : '-';
      },
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="orders" />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">Loading order details...</div>
        </main>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="orders" />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">Order not found</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="orders" />
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/orders')} className="h-8 w-8 shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-2xl font-heading font-bold text-primary truncate">
              {order.order_number}
            </h1>
            <Badge variant={getStatusBadgeVariant(order.status)}>
              {order.status.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>
          <Button variant="outline" size="sm" className="ml-auto shrink-0" onClick={() => navigate(`/orders/${id}/edit`)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Order
          </Button>
        </div>

        {/* Order Header Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Order Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-start gap-2">
                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <div className="text-sm text-muted-foreground">Customer</div>
                  <div className="font-medium">{order.customer_name}</div>
                  {order.customer_email && (
                    <div className="text-sm text-muted-foreground">{order.customer_email}</div>
                  )}
                  {order.customer_phone && (
                    <div className="text-sm text-muted-foreground">{order.customer_phone}</div>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <div className="text-sm text-muted-foreground">Order Date</div>
                  <div className="font-medium">{formatDate(order.order_date)}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <div className="text-sm text-muted-foreground">Required By</div>
                  <div className="font-medium">{formatDate(order.required_by_date)}</div>
                </div>
              </div>
            </div>
            {order.special_instructions && (
              <div className="mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground mb-1">Special Instructions</div>
                <div className="text-sm">{order.special_instructions}</div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order Items Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              <CardTitle>Order Items ({orderItems.length})</CardTitle>
            </div>
            <CardDescription>Products and rental details</CardDescription>
          </CardHeader>
          <CardContent>
            {orderItems.length > 0 ? (
              <div className="space-y-3">
                {orderItems.map((item, index) => {
                  const primaryImage = item.asset_template?.images?.find(img => img.variant === 'web');
                  const imageUrl = primaryImage?.url || item.asset_template?.images?.[0]?.url;

                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-4 p-4 border rounded-lg bg-card"
                    >
                      {/* Asset Image */}
                      {imageUrl ? (
                        <div className="flex-shrink-0 w-20 h-20 bg-muted rounded-lg overflow-hidden">
                          <img
                            src={imageUrl}
                            alt={item.asset_template?.name || 'Product'}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="flex-shrink-0 w-20 h-20 bg-muted rounded-lg flex items-center justify-center">
                          <Package className="h-8 w-8 text-muted-foreground" />
                        </div>
                      )}

                      {/* Product Details */}
                      <div className="flex-1">
                        <div className="font-medium">
                          {item.asset_template?.name || 'Product'}
                          {item.asset_template?.model_number && (
                            <span className="text-sm text-muted-foreground ml-2">
                              ({item.asset_template.model_number})
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Qty: {item.quantity} × £{formatCurrency(Number(item.unit_price))}/month | {getRentalLabel(item.rental_length_months)}
                        </div>
                      </div>

                      {/* Price */}
                      <div className="text-right flex-shrink-0">
                        <div className="font-semibold text-lg">
                          £{formatCurrency(Number(item.total_price))}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Total revenue ({item.rental_length_months} months)
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div className="flex justify-between items-center pt-3 border-t font-bold text-lg">
                  <span>Total:</span>
                  <span className="text-2xl text-primary">£{formatCurrency(calculateTotal())}</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No items in this order
              </div>
            )}
          </CardContent>
        </Card>

        {/* Addresses Card */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Home className="h-5 w-5" />
                <CardTitle>Billing Address</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{formatAddress(order.billing_address)}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <CardTitle>Delivery Address</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{formatAddress(order.delivery_address)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Related Jobs Card */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                <CardTitle>Related Jobs ({jobs.length})</CardTitle>
              </div>
              <Button onClick={() => setCreateJobModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Job
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {jobs.length > 0 ? (
              <DataTable
                columns={jobColumns}
                data={jobs}
                loading={false}
                emptyMessage="No jobs for this order yet"
              />
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No jobs created yet. Click "Create Job" to get started.
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create Job Modal */}
      <CreateJobModal
        open={createJobModalOpen}
        onOpenChange={setCreateJobModalOpen}
        onSuccess={handleJobCreated}
        preSelectedOrderId={id}
        lockOrder={true}
      />
    </div>
  );
};

export default OrderDetails;
